import { z } from 'zod';

/**
 * How — and whether — a **non-owner member** may be minted a read url for a kind
 * of media (N12).
 *
 * The axis that decides which branch of the read ladder a member falls down, and
 * it is three values rather than a boolean because the platform stores two
 * genuinely different kinds of member-readable thing:
 *
 * - **`none`** — no member-to-member path exists at all. Identity evidence
 *   (`id_document`, `selfie`) and every permission-reached platform asset
 *   (`brand_asset`, `payment_evidence`) sit here. There is no allowing branch to
 *   reach; a member gets the same 404 a stranger does.
 * - **`owner_policy`** — a member may reach it *only* through the owner's
 *   photo-privacy ladder (P17a): both accounts eligible, no block in either
 *   direction, and either the owner's `photoVisibility` is `visible` or an
 *   active match exists. Requires an owner, so it is only ever declared on a
 *   `member`-owned kind.
 * - **`open`** — the asset belongs to the deployment and is part of what the
 *   product shows its members, so being authenticated is the whole check. There
 *   is no owner, therefore no privacy policy to consult and no `ownerUserId` to
 *   read. Only ever declared on a `platform`-owned kind.
 *
 * ## Why a declared enum rather than a boolean, and why not derived
 *
 * This replaced `isMemberReadable: boolean` in P25a2, when venue photographs
 * needed a kind that is platform-owned **and** member-readable — a pairing the
 * boolean could not express, because its single allowing branch handed
 * `ownerUserId` to the photo ladder and a platform asset has none.
 *
 * The old flag's warning survives the change intact and applies to all three
 * values: **declared, never derived.** The tempting inference is
 * `ownership === 'member'`, which is equally true of `id_document` and `selfie`
 * — taking it would put a member's identity evidence behind a ladder that has an
 * allowing branch. A kind reaches a member because somebody wrote the mode down,
 * never because of a property it happens to share with one that does.
 *
 * `open` is deliberately **not** "public": nothing here makes an object readable
 * without a session, and staff still reach an `open` kind through its
 * `staffPermission` like every other kind. It is a statement about members.
 *
 * ## What this axis makes sayable that the boolean could not
 *
 * The standing question *"may a wali see the counterparty's photograph"* is a
 * request for a **fourth** mode — a ladder run against somebody other than the
 * viewer. It is not answered here, and this file is not the place to answer it
 * (see `PROGRESS.md` §Known issues for what its owner owes: the abuse argument
 * and the operator control). What changed is only that the registry can now
 * state such a rule explicitly instead of overloading a yes/no.
 */
export const MEMBER_READ_MODES = ['none', 'owner_policy', 'open'] as const;

export const MemberReadSchema = z.enum(MEMBER_READ_MODES);

export type MemberRead = (typeof MEMBER_READ_MODES)[number];

const MODE_SET: ReadonlySet<string> = new Set<string>(MEMBER_READ_MODES);

/** Narrows a plain string. Fail-closed: an unknown mode is not a mode. */
export function isMemberRead(value: string): value is MemberRead {
  return MODE_SET.has(value);
}
